/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import ComponentBase from '../ComponentBase';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Component used to scale or resize node. */
@cc._decorator.ccclass
export default class AutoResizingComponent extends ComponentBase
{
    /** Percentage to scale to. */
    @property({ type: cc.Float, visible: true })
    private _scalePercentage: number = 100.0;
    /** Local getter for decimal value of scale. */
    protected get scaleDecimal(): number { return this._scalePercentage / 100.0; }

    @property(cc.Boolean)
    protected perserveStartSize: boolean = false;
    @property(cc.Float)
    protected scaleDampener: number = 0;

    /** cc.Node to scale in comparison to. */
    @property({ type: cc.Node, visible: true })
    private _scaleToNode: cc.Node = null;

    /** Map x to y and y to x when scaling relative to rotated objects. */
    @property({ visible: true })
    private _swapAxes: boolean = false;

    /** If true, scales the width. */
    @property({ visible: true })
    private _scaleWidth: boolean = true;

    /** If true, scales the height. */
    @property({ visible: true })
    private _scaleHeight: boolean = false;

    /** If true, will maintain and fixed height width ratio. */
    @property({ visible: true })
    private _fixedRatio: boolean = true;

    /** If true, will set size instead of scale. */
    @property()
    protected setSize: boolean = false;

    /** If true, will swap heigh and width constraints on orientation change. */
    @property()
    protected respondToOrientation: boolean = false;

    private _portaitShouldScale: [boolean, boolean] = [false, false];
    private _landscapeShouldScale: [boolean, boolean] = [false, false];

    // protected autoResizingCanvasRef: InjectableComponent<AutoResizingCanvas>
    //     = new InjectableComponent<AutoResizingCanvas>();

    /** Local getter for x scalar. */
    protected get capturedXScalar(): number
    {
        return this._swapAxes
            ? (this._scaleToNodeSize.height * this.scaleDecimal) / this._nodeSize.width
            : (this._scaleToNodeSize.width * this.scaleDecimal) / this._nodeSize.width;
    }
    /** Local getter for y scalar. */
    protected get capturedYScalar(): number
    {
        return this._swapAxes
            ? (this._scaleToNodeSize.width * this.scaleDecimal) / this._nodeSize.height
            : (this._scaleToNodeSize.height * this.scaleDecimal) / this._nodeSize.height;
    }

    /** Current content size of node. */
    private _nodeSize: cc.Size;
    /** Current content size of scaleTo node. */
    private _scaleToNodeSize: cc.Size;
    /** If true, object is dirty. */
    private _hasChanged: boolean = true;

    private _startScaleToSize: cc.Size;
    private _startSize: cc.Size;

    /** @hidden */
    protected setContexts(): void
    {
        // this.autoResizingCanvasRef.SetContext(AutoResizingCanvas);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        if (!this._scaleToNode)
        {
            this._scaleToNode = cc.find('Canvas');
        }

        if (this.respondToOrientation)
        {
            // if (this.autoResizingCanvasRef.Value)
            // {
            //     this.autoResizingCanvasRef.Value
            //         .AddOnOrientationChangeHandler(this.onOrientationChangedHandler.bind(this));
            // }

            this._landscapeShouldScale = [this._scaleWidth, this._scaleHeight];
            this._portaitShouldScale = [!this._scaleWidth, !this._scaleHeight];

            // Update to initial value.
            // this.onOrientationChangedHandler();
        }

        this.captureSizes();

        this._scaleToNode.on('size-changed', () =>
        {
            if (this.captureSizes()._hasChanged)
            {
                this.setScaleToCapture();
            }
        });
    }

    // /** Repsond to orientation event to flip height vs width constraints. */
    // private onOrientationChangedHandler(): void
    // {
    //     let shouldScale: [boolean, boolean] = this.autoResizingCanvasRef.Value.IsLandScape
    //         ? this._landscapeShouldScale
    //         : this._portaitShouldScale;

    //     this._scaleWidth = shouldScale[0];
    //     this._scaleHeight = shouldScale[1];

    //     this.update();
    // }

    /** Captures currenct size of node and scaleTo node. */
    private captureSizes(): AutoResizingComponent
    {
        const newScaleToNodeSize: cc.Size = (this._scaleToNode)
                                ? this._scaleToNode.getContentSize()
                                : cc.director.getWinSizeInPixels();

        this._nodeSize = this.node.getBoundingBoxToWorld();

        this._startSize = this.node.getContentSize();
        this._startScaleToSize = this._scaleToNode.getContentSize();

        if (!this._scaleToNodeSize)
        {
            this._scaleToNodeSize = newScaleToNodeSize;
        }

        if (this._scaleToNodeSize.width === newScaleToNodeSize.width
            && this._scaleToNodeSize.height === newScaleToNodeSize.height)
        {
            return this;
        }

        this._scaleToNodeSize = newScaleToNodeSize;

        this._hasChanged = true;
        return this;
    }

    /** Sets scale of this node to the captured results. */
    private setScaleToCapture(): AutoResizingComponent
    {
        let newScale: cc.Vec2 = cc.Vec2.ZERO;
        newScale.x = (this._scaleWidth)
            ? this.capturedXScalar
            : (this._fixedRatio) ? this.capturedYScalar : 1;

        newScale.y = (this._scaleHeight)
            ? this.capturedYScalar
            : (this._fixedRatio) ? this.capturedXScalar : 1;

        if (newScale.x !== 0 && newScale.y !== 0)
        {
            if (this.setSize)
            {
                let width: number;
                let height: number;

                if (this.perserveStartSize)
                {
                    const scales: cc.Size = this.getScaledSize();

                    width = this._startSize.width * scales.width;
                    height = this._startSize.height * scales.height;
                }
                else
                {
                    width = this._scaleToNode.width * this.scaleDecimal;
                    height = newScale.y * this.node.height;
                }

                this.node.setContentSize(width, height);
            }
            else
            {
                this.node.setScale(newScale);
            }
        }

        this._hasChanged = false;
        this._nodeSize = this.node.getBoundingBoxToWorld();

        return this;
    }

    private getScaledSize(): cc.Size
    {
        let scaleWidth: number = (this._scaleToNodeSize.width / this._startScaleToSize.width);
        if (this.scaleDampener && scaleWidth !== 1)
        {
            scaleWidth = ((scaleWidth - 1) / this.scaleDampener) + 1;
        }

        let scaleHeight: number = (this._scaleToNodeSize.height / this._startScaleToSize.height);
        if (this.scaleDampener && scaleHeight !== 1)
        {
            scaleHeight = ((scaleHeight - 1) / this.scaleDampener) + 1;
        }

        return new cc.Size(scaleWidth, scaleHeight);
    }
}
